import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import useUserData from 'hooks/useUserData';
import { Box } from '@mui/system';
import { Chip, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArticleType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import KnowledgeAPI from 'api/KnowledgeAPI';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';

type RenderSuggestionStatusChipsProps = {
  article: ArticleType;
};

const statusMapping = {
  Open: 'NEW',
  Closed: 'CLOSED',
};

function RenderSuggestionStatusChips({ article }: RenderSuggestionStatusChipsProps) {
  const { taskStatus, id } = article;
  const { companyId, isNovaOrCompanyAdmin, user } = useUserData();
  const [status, setStatus] = useState(taskStatus);
  const [loadingStatus, setLoadingStatus] = useState<string | null>(null);

  // Sync status when taskStatus changes
  useEffect(() => {
    setStatus(taskStatus);
  }, [taskStatus]);

  const updateSuggestionStatus = useCallback(
    async (selectedStatus: string) => {
      try {
        setLoadingStatus(selectedStatus);
        const updatedStatus = status === 'NEW' ? 'CLOSED' : 'NEW';
        await KnowledgeAPI.editArticle(companyId, id, { ...article, taskStatus: updatedStatus });
        setStatus(updatedStatus);
        toast.success('Suggestion updated successfully!');
      } catch (error) {
        toast.error(catchErrorMessage(error));
      } finally {
        setLoadingStatus(null);
      }
    },
    [status, companyId, id, article],
  );

  const canEditStatus = useMemo(() => {
    return isNovaOrCompanyAdmin || user?.username === article?.createdBy;
  }, [isNovaOrCompanyAdmin, user, article?.createdBy]);

  return Object.values(statusMapping)?.includes(status || '') ? (
    <Fragment key={status}>
      <Title>Status</Title>
      <Box>
        {['Open', 'Closed'].map((label) => {
          const mappedStatus = statusMapping[label];
          const isSelected = status === mappedStatus;
          const isLoading = loadingStatus === mappedStatus;
          return (
            <Chip
              key={label}
              label={
                isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 24,
                      height: 24,
                    }}
                  >
                    <CircularProgress size={16} color="inherit" />
                  </Box>
                ) : (
                  label
                )
              }
              sx={(theme) => ({
                mt: 0.5,
                mr: 1,
                mb: 1,
                width: 70,
                fontWeight: 700,
                backgroundColor: isSelected
                  ? theme.palette.custom.tuna
                  : theme.palette.custom.white,
                color: isSelected ? theme.palette.custom.white : theme.palette.custom.tuna,
                border: isSelected ? 'none' : `1px solid ${theme.palette.custom.smoky}`,
                fontSize: '13px',
                '&:hover': {
                  cursor: canEditStatus ? 'pointer' : 'default',
                  backgroundColor: isSelected
                    ? theme.palette.custom.tuna
                    : theme.palette.custom.white,
                },
              })}
              {...(canEditStatus && {
                onClick: () =>
                  canEditStatus &&
                  !loadingStatus &&
                  status !== mappedStatus &&
                  updateSuggestionStatus(mappedStatus),
              })}
              disabled={isLoading}
            />
          );
        })}
      </Box>
    </Fragment>
  ) : null;
}
export const Title = styled(Typography, { label: 'Title' })({
  fontSize: 14,
  fontWeight: 700,
  marginTop: 12,
});

export default RenderSuggestionStatusChips;
